import { A, S } from '@mobily/ts-belt'
import * as Sentry from '@sentry/vue'
import { VueQueryPlugin } from '@tanstack/vue-query'
import { plugin as unleashPlugin } from '@unleash/proxy-client-vue'
import { setDefaultOptions } from 'date-fns'
import { pl } from 'date-fns/locale'
import { createNotivue } from 'notivue'
import { createPinia } from 'pinia'
import { createApp } from 'vue'

import App from '@/App.vue'
import { router } from '@/router'

import { authService } from './services/authService'
import { queryClient } from './services/queryClient'

import '@/assets/css/fonts.css'
import '@/assets/css/driver.css'
import '@/assets/css/variables.css'
import '@/components/ui/theme.css'
import '@repo/ui/reset.css'
import 'virtual:uno.css'

import { G } from '@mobily/ts-belt'

import { driverPlugin } from './plugins/driverJS'

setDefaultOptions({ locale: pl })

// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
const app = createApp(App)
const pinia = createPinia()
const notivue = createNotivue({
  avoidDuplicates: true,
  enqueue: true,
  limit: 3,
  position: 'bottom-right',
  transition: 'transform 0.35s cubic-bezier(0.5, 1, 0.25, 1)',
})

app.use(notivue)
app.use(router)
app.use(pinia)
app.use(driverPlugin, {
  allowClose: false,
  doneBtnText: 'Zamknij',
  nextBtnText: 'Dalej',
  prevBtnText: 'Wstecz',
  stagePadding: 3,
})
app.use(VueQueryPlugin, {
  queryClient,
})

const ALLOWED_ATTRIBUTES = [
  'sentry',
  'test-id',
  'testId',
  'cy',
  'component',
  'qa',
  'data-component',
]

Sentry.init({
  beforeBreadcrumb(breadcrumb, hint) {
    if (breadcrumb.category?.startsWith('ui') && hint) {
      let target = (hint.event as Event).target as HTMLInputElement

      if (breadcrumb.category === 'ui.click') {
        const descriptors = []
        const msg = '<Clicks on:'

        let parentsVisited = 0
        let attributes = ''

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        while (target && parentsVisited < 10) {
          if (
            target.localName &&
            (target.localName == 'a' || target.localName.includes('button'))
          ) {
            // Collect inner text, to try to identify the button
            descriptors.push(target.localName)
            if (target.innerText) {
              descriptors.push(`"${target.innerText}"`)
            }
          }

          if (target.localName == 'input') {
            descriptors.push(target.localName)
            if (target.placeholder.length > 0) {
              descriptors.push(`"${target.placeholder}"`)
            }
            if (target.accept.length > 0) {
              descriptors.push(`"${target.accept}"`)
            }
          }
          Object.keys(target.dataset)
            .filter(v => ALLOWED_ATTRIBUTES.includes(v))
            .forEach(key => {
              attributes += `[data-${key}="${target.dataset[key]}"]`
            })

          target = target.parentNode as HTMLInputElement
          parentsVisited++
        }
        if (S.isNotEmpty(attributes) || A.isNotEmpty(descriptors)) {
          breadcrumb.message = `${msg} ${descriptors.join(' ')} ${attributes}>`
        }
      }
    }

    return breadcrumb
  },
  dsn: import.meta.env.VUE_APP_SENTRY_DSN,
  environment: import.meta.env.VUE_APP_SENTRY_ENV,
  ignoreErrors: ['timeout exceeded', 'Request failed with status code 401'],
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration(),
    Sentry.extraErrorDataIntegration(),
  ],
  replaysOnErrorSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  tracesSampleRate: 1.0,
  tunnel: import.meta.env.VUE_APP_SENTRY_TUNNEL,
  Vue: app,
})

const unleashConfig = {
  appName: 'kcalmar-app',
  clientKey: import.meta.env.VUE_APP_UNLEASH_CLIENT_KEY,
  refreshInterval: 15,
  url: 'https://flags.kcalmar.com/api/frontend',
}

app.use(unleashPlugin, { config: unleashConfig })

app.mount('#app')

window.addEventListener('storage', event => {
  if ((event.key === 'accessToken' || G.isNullable(event.key)) && G.isNullable(event.newValue)) {
    authService.deleteTokens()
    authService.redirectToLogoutPage()
  }
})
